import React, { useState } from 'react';
import { useSubscription } from '../../hooks/useSubscription';
import UpgradeModal from './UpgradeModal';

/**
 * A reusable wrapper that blocks UI elements or routes based on subscription entitlements.
 * @param {string} feature - The FEATURE_KEY to check (e.g., 'FEATURE_VISITOR')
 * @param {boolean} hide - If true, the component returns null instead of showing the UpgradeModal (useful for sidebars)
 */
const FeatureGate = ({ feature, hide = false, children, upgradeMessage = 'Upgrade your plan to unlock this feature.' }) => {
    const { hasFeature, isLoading } = useSubscription();
    const [showModal, setShowModal] = useState(false);

    if (isLoading) {
        return <div className="animate-pulse bg-slate-200 dark:bg-slate-800 h-10 w-full rounded"></div>; // Skeleton loader
    }

    if (hasFeature(feature)) {
        return <>{children}</>;
    }

    if (hide) {
        return null;
    }

    return (
        <div className="relative group">
            {/* Disabled Overlay */}
            <div 
                className="absolute inset-0 bg-white/60 dark:bg-slate-950/60 z-10 cursor-not-allowed flex items-center justify-center backdrop-blur-[1px]"
                onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setShowModal(true);
                }}
            >
                <div className="bg-gray-800 text-white px-3 py-1 rounded-full text-xs font-semibold transition-opacity flex items-center gap-1 shadow-lg">
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"></path></svg>
                    Premium Feature
                </div>
            </div>
            
            {/* The original content, visibly disabled */}
            <div className="opacity-50 pointer-events-none select-none">
                {children}
            </div>

            {showModal && (
                <UpgradeModal 
                    featureKey={feature}
                    message={upgradeMessage}
                    onClose={() => setShowModal(false)}
                />
            )}
        </div>
    );
};

export default FeatureGate;
